import React, { useMemo } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useCounterpartyNames } from '../Hooks/useCounterpartyNames';

/**
 * Pick one operator or one partner from the list — never by typing an id.
 *
 * The two are separate directories (`/operators` and `/bookings/partners`),
 * so one component covers both rather than two near-identical ones: the
 * `kind` prop chooses which directory the options come from, and the labels
 * read the same way either way.
 *
 * `useCounterpartyNames` already fetches and caches both lists at module
 * level for the Payments screen, so mounting this costs no extra request on
 * a page that has already resolved a name.
 *
 * ON LABELS: names are shown EXACTLY as stored. Operator records carry the
 * office's own decorations — `AU-AA-670 - Tóth Imre`, `Diós Imre - EUR`,
 * `Golden Transfer- IL's Transport_EUR` — and those decorations are how
 * somebody finds the record they mean. Stripping the account code or the
 * currency suffix to make the list "tidy" would hide exactly the thing being
 * searched for. A typeahead is used rather than a 150-option `<select>` for
 * the same reason: the decoration is what gets typed.
 */

/** A counterparty's display name, as stored. Shared with the document list. */
export function counterpartyLabel(doc) {
  // Both spellings appear in the wild; the Payments screen reads them the
  // same defensive way.
  return doc?.name || doc?.Name || '';
}

export const KIND_LABELS = { operator: 'Operator', partner: 'Partner' };

/**
 * `{ id, label }` for every counterparty of one kind, name-sorted.
 * Exported so a caller that needs the same list without the input (e.g. to
 * resolve a filter's current value) does not build its own.
 */
export function useCounterpartyOptions(kind) {
  const { partnersById, operatorsById, loading } = useCounterpartyNames();
  const source = kind === 'partner' ? partnersById : operatorsById;

  // The maps are rebuilt on every render of the hook, so the memo keys off
  // their SIZE and the kind rather than identity — keyed on the map itself it
  // would recompute every render and hold nothing.
  const sourceSize = source.size;
  const options = useMemo(
    () => Array.from(source.entries())
      .map(([id, doc]) => ({ id, label: counterpartyLabel(doc) || id, doc }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [kind, sourceSize], // eslint-disable-line
  );

  return { options, loading };
}

function CounterpartyPicker({
  kind,
  value = '',
  onChange,
  id = 'counterparty-picker',
  label = null,
  placeholder = null,
  disabled = false,
  /** Shown as the first option; selecting it clears the value. */
  allowAny = false,
  anyLabel = 'Any',
  isInvalid = false,
  feedback = null,
}) {
  const { options, loading } = useCounterpartyOptions(kind);
  const kindLabel = KIND_LABELS[kind] || 'Counterparty';

  const menuOptions = useMemo(() => (
    allowAny ? [{ id: '', label: anyLabel }, ...options] : options
  ), [allowAny, anyLabel, options]);

  // The typeahead is a controlled multi-select under the hood: `selected` is
  // an array, and an id we hold but cannot find in the options (a
  // counterparty that has since been deactivated) still has to show as
  // something rather than silently reading as empty.
  const selected = useMemo(() => {
    if (!value) return [];
    const match = menuOptions.find((o) => o.id === value);
    return [match || { id: value, label: value }];
  }, [menuOptions, value]);

  return (
    <Form.Group controlId={id}>
      {label !== null && <Form.Label>{label}</Form.Label>}
      <Typeahead
        id={id}
        labelKey="label"
        options={menuOptions}
        selected={selected}
        isLoading={loading}
        disabled={disabled || loading}
        placeholder={placeholder || (loading
          ? `Loading ${kindLabel.toLowerCase()}s…`
          : `Search ${kindLabel.toLowerCase()}s by name…`)}
        onChange={(picked) => {
          const next = picked[0] || null;
          onChange(next?.id || '', next?.label || '');
        }}
        minLength={0}
        clearButton
        maxResults={50}
        isInvalid={isInvalid}
        inputProps={{ 'aria-label': label || `${kindLabel} name` }}
      />
      {loading && (
        <div className="text-muted small mt-1">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading the
          {' '}
          {kindLabel.toLowerCase()}
          {' '}
          list…
        </div>
      )}
      {!loading && (
        <Form.Text className="text-muted">
          {`${options.length} ${kindLabel.toLowerCase()}${options.length === 1 ? '' : 's'} · names shown exactly as stored`}
        </Form.Text>
      )}
      {isInvalid && feedback && (
        <div className="text-danger small mt-1">{feedback}</div>
      )}
    </Form.Group>
  );
}

export default CounterpartyPicker;
